import Link from 'next/link';
import { useRouter } from 'next/navigation';
import React, { MouseEventHandler, useCallback, useRef } from 'react';

import styled from '@emotion/styled';
import MuiMenu, { menuClasses } from '@mui/material/Menu';
import MuiMenuItem, { menuItemClasses } from '@mui/material/MenuItem';
import { useTranslations } from 'next-intl';
import { useBoolean } from 'usehooks-ts';

import BackgroundImage from '@web/app/(public)/collection/collection-background.png';
import { Button } from '@web/ui/components';
import { SharpImage } from '@web/ui/components/SharpImage';
import { ArrowLinkIcon } from '@web/ui/icons';

import { OPENSEA_PERSONA_BASE, OPENSEA_PERSONA_ETH } from '../const';

import { HubTile } from './HubTile';

const StyledHubTile = styled(HubTile)`
  position: relative;
  gap: ${({ theme }) => theme.spacing('2000')};

  picture {
    position: absolute;
    inset: 0;
  }
`;

const Background = styled(SharpImage)`
  object-fit: cover;
  width: 100%;
  height: 100%;
`;

const TextContent = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: ${({ theme }) => theme.spacing('0750')};
  z-index: 1;
`;

const Subtitle = styled.span`
  color: ${({ theme }) => theme.color.onYellow};
  font: ${({ theme }) => theme.typography.body.bold};
  font-size: ${({ theme }) => theme.typescale.size['9']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.condensed};
`;

const Description = styled.span`
  color: ${({ theme }) => theme.color.onYellow};
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing('0500')};
  z-index: 1;
`;

const Menu = styled(MuiMenu)`
  .${menuClasses.paper} {
    margin-top: ${({ theme }) => theme.spacing('0500')};
    background-color: ${({ theme }) => theme.color.surfaceContainer};
    border-radius: ${({ theme }) => theme.shape.corner['1000']};
    box-shadow: ${({ theme }) => theme.elevation.level1};
  }
`;

const MenuItem = styled(MuiMenuItem)`
  &.${menuItemClasses.root} {
    display: flex;
    justify-content: space-between;
    color: ${({ theme }) => theme.color.onSurface};
    font: ${({ theme }) => theme.typography.body.regular};
    font-size: ${({ theme }) => theme.typescale.size['0875']};

    padding-block: ${({ theme }) => theme.spacing('0750')};
    padding-inline: ${({ theme }) => theme.spacing('1000')};
  }
`;

const StyledArrowLinkIcon = styled(ArrowLinkIcon)`
  margin-left: ${({ theme }) => theme.spacing('1000')};
  height: ${({ theme }) => theme.size['0875']};
  width: ${({ theme }) => theme.size['0875']};
`;

export const CollectionHubTile = () => {
  const t = useTranslations();
  const anchorEl = useRef<HTMLButtonElement | null>(null);
  const { push } = useRouter();

  const {
    value: open,
    setTrue: showPopup,
    setFalse: hidePopup,
  } = useBoolean(false);

  const handleClose = useCallback<MouseEventHandler>((e) => {
    e.stopPropagation();
    hidePopup();
  }, []);

  const handleBuyClick = useCallback<MouseEventHandler>((e) => {
    e.stopPropagation();
    showPopup();
  }, []);

  return (
    <StyledHubTile
      background="yellow"
      textColor="onYellow"
      stateLayer="onYellow"
      title={t('COLLECTION_HUB_TILE_TITLE')}
      icon="link"
      onClick={() => push('/collection')}
    >
      <Background src={BackgroundImage} />
      <TextContent>
        <Subtitle>{t('COLLECTION_HUB_TILE_SUBTITLE')}</Subtitle>
        <Description>{t('COLLECTION_HUB_TILE_DESCRIPTION')}</Description>
      </TextContent>
      <Actions>
        <Button
          label={t('COLLECTION_HUB_BROWSE_COLLECTION_BUTTON')}
          variant="secondary"
          color="onBlue"
          background="blue"
          fullWidth
        />
        <Button
          ref={anchorEl}
          onClick={handleBuyClick}
          label={t('COLLECTION_HUB_BUY_PERSONA_BUTTON')}
          variant="secondary"
          color="onGreen"
          background="green"
          fullWidth
        />
        <Menu
          anchorEl={anchorEl.current}
          open={open}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          onClose={handleClose}
        >
          <Link
            href={OPENSEA_PERSONA_BASE}
            rel="noreferrer"
            target="_blank"
            onClick={(e) => e.stopPropagation()}
          >
            <MenuItem>
              {t('CTA_BUY_PERSONA_BASE')} <StyledArrowLinkIcon size="0500" />
            </MenuItem>
          </Link>
          <Link
            href={OPENSEA_PERSONA_ETH}
            rel="noreferrer"
            target="_blank"
            onClick={(e) => e.stopPropagation()}
          >
            <MenuItem>
              {t('CTA_BUY_PERSONA_ETH')} <StyledArrowLinkIcon size="0500" />
            </MenuItem>
          </Link>
        </Menu>
      </Actions>
    </StyledHubTile>
  );
};
